<template>
  <div>
    <div
      class="relative estimation-result"
      :class="{ '-refreshing': !request.received }"
    >
      <saving-overlay
        opacity="10"
        text-color="text-action"
        class="text-4xl rounded"
        size="text-6xl"
        v-if="request.sent && !request.received && !errors"
      ></saving-overlay>

      <div v-if="estimation" class="p-3">
        <div class="flex justify-between items-stretch pb-2">
          <div class="text-center py-1 pl-1 pr-5 flex flex-col justify-between">
            <p
              class="whitespace-no-wrap text-primary font-bold pb-1 flex"
              v-text="i18n.computer"
            ></p>
            <div class="py-3 h-16 items-center flex">
              <!-- <font-awesome-icon
              :icon="['far', 'turtle']"
              size="2x"
              class="mx-2 text-gray-700"
              fixed-width
            ></font-awesome-icon> -->
              <span
                v-text="estimation.user.duration_str"
                class="text-sm text-secondary"
              ></span>
            </div>
          </div>
          <div
            class="text-center bg-gray-100 rounded border border-gray-200 p-1 flex flex-col justify-between"
          >
            <p class="text-primary font-bold pb-1" v-text="i18n.with_ranch"></p>
            <div class="md:flex h-16">
              <div
                v-if="bestDuration.str.duration !== worstDuration.str.duration"
                class="flex items-center"
              >
                <font-awesome-icon
                  :icon="['fas', 'stopwatch']"
                  size="2x"
                  class="mx-2 text-gray-700"
                  fixed-width
                ></font-awesome-icon>
                <span class="text-secondary text-sm text-left"
                  >{{ i18n.between }} {{ bestDuration.str.duration }}
                  <span class="inline-block"
                    >{{ i18n.and }} {{ worstDuration.str.duration }}</span
                  ></span
                >
              </div>
              <div v-else class="flex items-center">
                <font-awesome-icon
                  :icon="['fas', 'stopwatch']"
                  size="2x"
                  class="mx-2 text-gray-700"
                  fixed-width
                ></font-awesome-icon>
                <span
                  v-text="bestDuration.str.duration"
                  class="text-secondary"
                ></span>
              </div>
              <div
                class="flex items-center"
                v-if="bestPrice.str.discounted !== worstPrice.str.discounted"
              >
                <font-awesome-icon
                  :icon="['fas', 'coins']"
                  size="2x"
                  class="mx-2 text-gray-700"
                  fixed-width
                ></font-awesome-icon>
                <span class="text-secondary text-sm text-left">
                  <span
                    class="inline-block pr-1"
                    v-html="i18n.between + ' ' + bestPrice.str.discounted + ' '"
                  />
                  <span
                    class="inline-block"
                    v-html="
                      ' ' +
                      i18n.and +
                      ' ' +
                      worstPrice.str.discounted +
                      i18n.tax_excluded
                    "
                  />
                </span>
              </div>
              <div class="flex items-center" v-else>
                <font-awesome-icon
                  :icon="['fas', 'coins']"
                  size="2x"
                  class="mx-2 text-gray-700"
                  fixed-width
                ></font-awesome-icon>
                <span
                  class="text-secondary text-sm text-left"
                  v-html="
                    i18n.approximately +
                    ' ' +
                    bestPrice.str.discounted +
                    i18n.tax_excluded
                  "
                ></span>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-100 text-sm">
          <template v-if="estimation.first_project_notice">
            <EstimationTable
              :i18n="i18n"
              :priorities="estimation.priorities.slice(0, 1)"
              :bestPrice="bestPrice"
              :worstPrice="worstPrice"
              :selectedPriority="selectedPriority"
              @priority-selected="
                (priority) => $emit('priority-selected', priority)
              "
            />
            <details>
              <summary
                class="border-blue-200 bg-blue-100 text-blue-800 p-4 whitespace-pre-line"
                v-text="estimation.first_project_notice"
              ></summary>
              <EstimationTable
                :i18n="i18n"
                :priorities="estimation.priorities.slice(1)"
                :bestPrice="bestPrice"
                :worstPrice="worstPrice"
                :selectedPriority="selectedPriority"
                @priority-selected="
                  (priority) => $emit('priority-selected', priority)
                "
              />
            </details>
          </template>
          <EstimationTable
            v-else
            :i18n="i18n"
            :priorities="estimation.priorities"
            :bestPrice="bestPrice"
            :worstPrice="worstPrice"
            :selectedPriority="selectedPriority"
            @priority-selected="
              (priority) => $emit('priority-selected', priority)
            "
          />
        </div>

        <div class="p-2">
          <p
            v-for="note in priorityNotes"
            :key="note"
            class="text-sm p-1 text-gray-700"
            v-text="note"
          />
          <p
            class="text-sm p-1 text-gray-700"
            v-text="'** ' + i18n.max_node_note"
          />
        </div>
        <p
          class="p-3 rounded bg-blue-100 my-2 text-sm"
          v-html="i18n.ranch_credit_note"
        ></p>
      </div>
    </div>
    <div
      class="text-sm border rounded border-action p-2 text-gray-900 bg-orange-100"
    >
      <div class="flex justify-center py-3">
        <font-awesome-icon
          icon="exclamation-triangle"
          size="2x"
          class="text-action"
        ></font-awesome-icon>
        <h2
          class="text-center pl-3 text-lg"
          v-text="i18n.disclaimer.title"
        ></h2>
      </div>
      <ul class="text-justify pb-2 list-disc pl-4">
        <li
          v-for="(item, i) in i18n.disclaimer.items"
          :key="'disclaimer_' + i"
          v-html="item"
        ></li>
      </ul>
    </div>
  </div>
</template>

<script>
import EstimationTable from "./EstimationTable.vue";
import debounce from "lodash/debounce";
import axios from "axios";

export default {
  props: ["seconds", "images", "score", "renderer", "i18n", "selectedPriority"],
  components: {
    EstimationTable,
  },
  data() {
    return {
      estimation: null,
      errors: null,
      request: {
        sent: false,
        received: false,
        source: null,
      },
      updateEstimationLater: debounce(this.updateEstimation, 700),
    };
  },
  mounted() {
    this.willSendRequest();
    this.updateEstimationLater();
  },
  watch: {
    seconds() {
      this.willSendRequest();
      this.updateEstimationLater();
    },
    images() {
      this.willSendRequest();
      this.updateEstimationLater();
    },
    "score.cinebench"() {
      this.willSendRequest();
      this.updateEstimationLater();
    },
    "score.octanebench"() {
      this.willSendRequest();
      this.updateEstimationLater();
    },
    "renderer.id"() {
      this.updateEstimation();
    },
  },
  computed: {
    priorityNotes() {
      const notes = {};
      this.estimation.priorities.forEach((p) => {
        if (p.str.note) {
          notes[p.str.note] = 1;
        }
      });
      return Object.keys(notes);
    },
    isCPU() {
      return this.renderer.farms.includes("power");
    },
    isGPU() {
      return this.renderer.farms.includes("gpu");
    },
    requestParams() {
      // const type = this.isGPU && this.score.octanebench ? "gpu" : "cpu";
      const score = this.score;
      return {
        // type,
        score,
        images: this.images,
        seconds: this.seconds,
        farms: this.renderer.farms,
        majoration: this.renderer.majoration,
        locale: this.i18n.locale,
      };
    },
    bestDuration() {
      return this.bestPriority((a, b) => {
        return -(a.duration - b.duration);
      });
    },
    worstDuration() {
      return this.bestPriority((a, b) => {
        return a.duration - b.duration;
      });
    },
    bestPrice() {
      return this.bestPriority((a, b) => {
        return -(a.amount.discounted - b.amount.discounted);
      });
    },
    worstPrice() {
      return this.bestPriority((a, b) => {
        return a.amount.discounted - b.amount.discounted;
      });
    },
    prioritiesLength() {
      return Object.keys(this.estimation.priorities).length;
    },
  },
  methods: {
    choosePriority(priority) {
      this.$emit("prioritized", priority, this.estimation.user.ghzh);
    },
    bestPriority(cmp) {
      var best;
      this.estimation.priorities.forEach((p) => {
        if (!best || cmp(p, best) > 0) {
          best = p;
        }
      });
      return best;
    },
    willSendRequest() {
      this.request.sent = true;
      this.request.received = false;
      this.errors = null;
    },
    updateEstimation(displayError) {
      if (this.request.source) {
        this.request.source.cancel();
      }
      this.$emit("estimating");
      this.request.source = this.generateToken();
      this.willSendRequest();
      this.updateEstimationLater.cancel();
      return this._getApi(
        this.i18n.url.estimate,
        this.requestParams,
        this.request.source.token
      )
        .then((data) => {
          this.request.received = true;
          if (data.priorities) {
            data.priorities.sort((a, b) => {
              if (a.amount.render == b.amount.render) {
                return +a.amount.per_unit - +b.amount.per_unit;
              }
              return +a.amount.render - +b.amount.render;
            });
            var shrink = 1;
            data.priorities.forEach((p, i, arr) => {
              arr[i].shrink = shrink;
              shrink++;
            });
          }
          this.estimation = data;
          this.$emit("estimated");
          if (window.google_analytics_pageview) {
            window.google_analytics_pageview("/form-submit/tunnel/estimated");
          }
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return;
          }
          console.warn(error);
          this.request.received = true;
          this.estimation = null;
          if (displayError) {
            this.errors = error.response.data;
          }
        });
    },
    _getApi(url, params, cancelToken) {
      return (
        axios
          .get(url, { params, cancelToken })
          // eslint-disable-next-line no-unused-vars
          .then(function (response, reject) {
            if (response.status === 200) {
              return response.data;
            } else {
              var e = new Error("Not 200 response");
              e.code = response.statusText;
              e.config = response.config;
              e.response = response;
              throw e;
            }
          })
      );
    },
    generateToken: axios.CancelToken.source,
  },
};
</script>
